// src/components/loginpage/loginbox/Loginbox.jsx
//
// One form, no role tabs (Kuestion Campus contract §11). The identifier is a
// username, an email, or a student login ID. A student ID always matches
// XXXXXX-XXXXXX, which a username or email never does, so the form routes
// on that shape: student IDs go to /api/campus/student/login, everything
// else to /api/login. A wrong password reads the same either way.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Loginbox.css';
import { runLoginValidation } from './Loginbtnvalidator';
import { useAuth } from '../../../context/AuthContext';
import { useStudentAuthOptional } from '../../../context/StudentAuthContext';
import { STUDENT_LOGIN_ID } from '../../../config/apiConfig';
import { kq } from '../../../config/routes';

// Student login IDs are a Kuestion concept: only the Kuestion door routes on
// them (Separation contract v2.1 §5). The apex door is a plain platform login.
// `resumeTo`: where a student lands after signing in, when the sign-in was
// reached from an interrupted sitting (build brief §15). Otherwise the dashboard.
const Loginbox = ({ onNotify, onLoginSuccess, onForgotPassword, area = 'suite', resumeTo = null }) => {
  const isKuestion = area === 'kuestion';
  const navigate = useNavigate();
  const { login } = useAuth();
  const studentAuth = useStudentAuthOptional();
  const studentLogin = isKuestion && studentAuth ? studentAuth.login : null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [identifier, setIdentifier] = useState('');

  const isStudentId = Boolean(studentLogin) && STUDENT_LOGIN_ID.test(identifier.trim());

  const notifyMany = (msgs, type = 'warn', stepMs = 10) => {
    if (!onNotify || !Array.isArray(msgs)) return;
    msgs.forEach((m, i) => setTimeout(() => onNotify(m, type), i * stepMs));
  };

  const handleLoginClick = async () => {
    if (isSubmitting) return;

    const { ok, errors, values } = runLoginValidation();

    if (!ok) {
      notifyMany(errors, 'warn');
      return;
    }

    setIsSubmitting(true);
    onNotify && onNotify('Logging you in...', 'info');

    // ── Student ──
    if (studentLogin && STUDENT_LOGIN_ID.test(values.username)) {
      try {
        const s = await studentLogin(values.username, values.password);
        onNotify && onNotify(`Welcome, ${s.first_name || s.login_id}!`, 'success');
        setTimeout(() => navigate(resumeTo || kq('student'), { replace: true }), 600);
      } catch (err) {
        const reason = err.message || 'invalid_credentials';
        // Same message as a failed platform login. `invalid_login_format` is
        // never surfaced from the shared form (build note §1): the shape was
        // already matched locally, so it says nothing useful here.
        let msg = 'Invalid username or password.';
        if (reason === 'network_error') msg = "Couldn't reach the server. Check your connection and try again.";
        else if (reason === 'server_error') msg = 'Server error. Please try again shortly.';
        onNotify && onNotify(msg, 'error');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // ── Examiner / platform user ──
    try {
      const userData = await login(values.username, values.password);

      onNotify && onNotify(`Welcome back, ${userData.firstName}!`, 'success');

      // Small delay to show success message, then redirect
      setTimeout(() => {
        if (typeof onLoginSuccess === 'function') {
          onLoginSuccess(userData);
        }
      }, 800);
    } catch (err) {
      const reason = err.message || 'Login failed';

      // Map backend error reasons to user-friendly messages
      let userMessage = 'Login failed. Please try again.';
      if (reason === 'invalid_credentials') {
        userMessage = 'Invalid username or password.';
      } else if (reason === 'login_timeout') {
        userMessage = 'Server is taking too long. Please try again.';
      } else if (reason === 'missing_or_empty_username') {
        userMessage = 'Please enter your username.';
      } else if (reason === 'missing_or_empty_password') {
        userMessage = 'Please enter your password.';
      }

      onNotify && onNotify(userMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleLoginClick();
    }
  };

  return (
    <div className="loginbox">
      <div className="login-row login-item">
        <label htmlFor="login-username">{isKuestion ? 'Username, email, or student login ID' : 'Username or email'}</label>
      </div>
      <div className="login-row login-item">
        <div className="input-wrapper">
          <div className="left-part">
            <input
              id="login-username"
              type="text"
              placeholder={isKuestion ? 'you@example.com  or  RASTER-A7K2M9' : 'you@example.com'}
              autoComplete="username"
              autoCapitalize="off"
              spellCheck={false}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>
      {isKuestion && (
      <div className="login-row login-item">
        <span className="login-hint">
          <span className="login-hint__dot" aria-hidden="true" />
          <span>
            {isStudentId
              ? <>Student login &mdash; you will land in your exams.</>
              : <>One box for everyone. A student ID looks like <strong>RASTER-A7K2M9</strong> &mdash; your school&rsquo;s prefix and your own code. Case does not matter.</>}
          </span>
        </span>
      </div>
      )}

      <div className="login-row login-item">
        <label htmlFor="login-password">Password</label>
      </div>
      <div className="login-row login-item">
        <div className="input-wrapper">
          <div className="left-part">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter password"
              autoComplete="current-password"
              onKeyPress={handleKeyPress}
              disabled={isSubmitting}
            />
          </div>
          <div className="right-part">
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                // Eye with a slash — password is currently visible
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M2 2l20 20" />
                  <path d="M10.6 10.6a2 2 0 002.8 2.8" />
                  <path d="M9.4 5.2A9.6 9.6 0 0112 5c5 0 9 4.4 10 7a15 15 0 01-3.5 4.5" />
                  <path d="M6.2 6.6C3.9 8.1 2.5 10.2 2 12c1 2.6 5 7 10 7a9.7 9.7 0 004.3-1" />
                </svg>
              ) : (
                // Plain eye — password is currently hidden
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="login-row login-item">
        <button
          className="login-button"
          onClick={handleLoginClick}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Logging in...' : 'Login'}
        </button>
      </div>

      <div className="login-row login-item">
        <span className="forgot-text" onClick={onForgotPassword}>Forgot password</span>
      </div>
    </div>
  );
};

export default Loginbox;
